'use client';

import { c as _c } from "react/compiler-runtime";
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { type Stripe, loadStripe } from '@stripe/stripe-js';
import { useTranslations, useLocale } from 'next-intl';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useEffectEvent, useRef, useState } from 'react';
import { stripeNativePaymentElementsOptions } from './components/formParts/SubmitButton/StripeNativePaymentButton';
import { useMessageContext } from '@/components/Messages';
import type { StripeOptions } from '@/donationForm/types';
import { NativePaymentMethodsProvider } from '@/donationPages/_dependencies/NativePaymentMethods';
import { useOfflineMessage } from '@/helpers/hooks';
import { withRetry } from '@/helpers/utils';
const StripeContext = createContext<StripeOptions>({});
export function useStripeContext() {
  return useContext(StripeContext);
}
export function useIsStripeLoaded() {
  const {
    stripe
  } = useStripeContext();
  return !!stripe;
}
export function useStripePromise(stripeKey: string) {
  const [promise, setPromise] = useState<Promise<Stripe | null> | null>(null);
  const {
    addMessage,
    removeMessage
  } = useMessageContext();
  const messageIdRef = useRef<string | null>(null);
  const removeErrorMessage = useCallback(() => messageIdRef.current && removeMessage(messageIdRef.current), [removeMessage]);
  const t = useTranslations('nextjs');
  const onError = useCallback((attempt: number) => {
    removeErrorMessage();
    messageIdRef.current = addMessage({
      type: 'error',
      message: `${t('donate.errors.stripe_loading')} ${t('errors.messages.retrying', {
        attempt
      })}`
    });
  }, [addMessage, removeErrorMessage, t]);
  const onFailure = useCallback(() => {
    removeErrorMessage();
    messageIdRef.current = addMessage({
      type: 'error',
      message: t('donate.errors.stripe_loading')
    });
  }, [addMessage, removeErrorMessage, t]);
  const loadStripeWithRetry = useEffectEvent(
  // false-positive
  // eslint-disable-next-line react-hooks/refs
  withRetry(loadStripe, {
    onError,
    onFailure,
    onSuccess: removeErrorMessage,
    baseDelayMs: 2500
  }));
  useEffect(() => {
    if (typeof window === 'undefined' || !stripeKey) return;
    const timeout = setTimeout(() => setPromise(loadStripeWithRetry(stripeKey)), 0);
    return () => {
      setPromise(null);
      clearTimeout(timeout);
    };
  }, [stripeKey]);
  return promise;
}
export function StripeContextProvider(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const stripe = useStripe();
  const elements = useElements();
  let t1;
  if ($[0] !== elements || $[1] !== stripe) {
    t1 = {
      stripe,
      elements
    };
    $[0] = elements;
    $[1] = stripe;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children || $[4] !== t1) {
    t2 = <StripeContext.Provider value={t1}>{children}</StripeContext.Provider>;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export function StripeProvider(t0) {
  const $ = _c(8);
  const {
    children,
    stripePublicKey
  } = t0;
  const stripePromise = useStripePromise(stripePublicKey);
  const locale = useLocale();
  useOfflineMessage();
  let t1;
  if ($[0] !== locale) {
    t1 = {
      locale,
      ...stripeNativePaymentElementsOptions
    };
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <StripeContextProvider>{children}</StripeContextProvider>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== stripePromise || $[5] !== t1 || $[6] !== t2) {
    t3 = <Elements stripe={stripePromise} options={t1}>{t2}</Elements>;
    $[4] = stripePromise;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function withStripeProvider<P extends {
  config: {
    stripePublicKey: string;
  };
}>(Component: React.ComponentType<P>) {
  return function WithStripeProvider(props: P) {
    return <StripeProvider stripePublicKey={props.config.stripePublicKey}>
        <NativePaymentMethodsProvider>
          <Component {...props} />
        </NativePaymentMethodsProvider>
      </StripeProvider>;
  };
}